import React, { memo } from 'react';
import { 
  Card, 
  Typography, 
  Link, 
  CardMedia, 
  Grid, 
  Box, 
  Rating, 
  IconButton, 
  Button,
  Chip,
  Avatar,
  CardContent,
  Stack,
  Tooltip,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  ChatBubbleOutline as ChatIcon,
  Launch as ExternalLinkIcon,
  Star as StarIcon,
  People as FollowersIcon,
  Visibility as ViewsIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import profilePhoto from '../../../Components/globalAssets/ppfLogo.png';
import {
  calculateAverageCreatorRate,
  calculateTotalFollowing,
  getAllPlatformLinks,
  platformToFollowing,
  platPromoToKey,
} from '../../../Utils/constants';

import TikTokIcon from '../../../Components/Icons/TikTok';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/Twitter';
import TwitchIcon from '../../../Components/Icons/Twitch';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import CastIcon from '@mui/icons-material/Cast';
import LinkIcon from '@mui/icons-material/Link';
import RankingBadge from '../../../Components/CreatorData/CreatorRanking';

import { formatCurrency, formatNumber, formatCompactNumber } from "../../../Utils/formatNumbers";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & STYLING
// ─────────────────────────────────────────────────────────────────────────────
const socialIcons = {
  TikTok: <TikTokIcon />,
  Instagram: <InstagramIcon />,
  Youtube: <YouTubeIcon />,
  X: <TwitterIcon />,
  Twitch: <TwitchIcon />,
  Podcasts: <PodcastsIcon />,
  Kick: <CastIcon />,
};

const platformColors = {
  Youtube: '#FF0000',
  Instagram: '#E4405F',
  TikTok: '#FE2C55',
  Podcasts: '#9C27B0',
  Twitch: '#9146FF',
  Kick: '#53FC18',
  X: '#1DA1F2',
};

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': { 
    color: theme.palette.primary.main,
  },
  '& .MuiRating-iconHover': { 
    color: theme.palette.primary.light,
  },
}));

const ModernCard = styled(Card, {
  shouldForwardProp: (prop) => !['platform', 'tierLevel', 'isSelected'].includes(prop),
})(({ theme, platform, tierLevel, isSelected }) => ({
  position: 'relative',
  borderRadius: 16,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  height: '100%',
  border: `2px solid ${isSelected 
    ? theme.palette.primary.main 
    : 'transparent'
  }`,
  
  background: isSelected 
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`
    : theme.palette.background.paper,
  
  ...(tierLevel === 'celebrity' && {
    background: `linear-gradient(135deg, ${alpha('#FFD700', 0.1)} 0%, ${alpha('#FFA500', 0.05)} 100%)`,
    border: '2px solid #FFD700',
    boxShadow: `0 0 20px ${alpha('#FFD700', 0.3)}`,
  }),
  
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[12],
    '& .platform-indicator': {
      transform: 'scale(1.1)',
    },
    '& .creator-avatar': {
      transform: 'scale(1.05)',
    },
    '& .action-buttons': {
      opacity: 1,
      transform: 'translateY(0)',
    }
  },
  
  '&::before': tierLevel === 'celebrity' ? {
    content: '""',
    position: 'absolute',
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    background: 'linear-gradient(45deg, #FFD700, #FFA500, #FF6347, #FFD700)',
    backgroundSize: '300% 300%',
    animation: 'gradient-shift 3s ease infinite',
    borderRadius: 18,
    zIndex: -1,
  } : {},
  
  '@keyframes gradient-shift': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
}));

const PlatformIndicator = styled(Box)(({ theme, platform }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(platformColors[platform] || theme.palette.primary.main, 0.9),
  color: 'white',
  backdropFilter: 'blur(10px)',
  border: `2px solid ${alpha('#fff', 0.2)}`,
  transition: 'all 0.3s ease',
  zIndex: 2,
}));

const TierBadge = styled(Chip)(({ theme, tier }) => ({
  position: 'absolute',
  top: 12,
  left: 12,
  fontSize: '0.7rem',
  fontWeight: 600,
  height: 24,
  borderRadius: 12,
  textTransform: 'uppercase',
  zIndex: 2,
  ...(tier === 'celebrity' && {
    background: 'linear-gradient(45deg, #FFD700, #FFA500)',
    color: '#000',
    fontWeight: 700,
  }),
  ...(tier === 'premium' && {
    background: 'linear-gradient(45deg, #9C27B0, #673AB7)',
    color: '#fff',
  }),
}));

/**
 * CreatorContainers - Modern creator card grid component
 */
function CreatorContainers({
  creators,
  platform,
  promotion,
  onAddToCampaign,
  onChatWithCreator,
  selectedItems = new Set(),
  onInviteToPartnership = null,
}) {
  const theme = useTheme();

  const parseNumber = (numStr) => {
    const number = parseInt(numStr, 10);
    return isNaN(number) ? 0 : number;
  };

  const formatFollowers = (count) => {
    return count >= 1000000 ? formatCompactNumber(count) : formatNumber(count);
  };

  return (
    <Grid container spacing={3}>
      {creators.map((creator) => {
        const isSelected = selectedItems.has(creator.creator);
        const followerCount = platform
          ? parseNumber(creator[platformToFollowing[platform]] || '0')
          : calculateTotalFollowing(creator);

        const avgReview = creator.average_rating || 0;
        const reviewCount = creator.review_count || 0;

        const rate = platform && promotion
          ? creator[platPromoToKey[platform]?.[promotion]]
          : calculateAverageCreatorRate(creator);

        const creatorPlatforms = getAllPlatformLinks(creator);
        const hasCelebTier = creator.tier_level === 'celebrity';

        return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={creator.creator}>
              <ModernCard
                platform={platform}
                tierLevel={creator.tier_level}
                isSelected={isSelected}
                onClick={() => onAddToCampaign(creator.creator)}
              >
                {/* NEW: Ranking Badge */}
                {creator.ranking_metadata && (
                  <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 3 }}>
                    <RankingBadge
                      ranking={creator.ranking_metadata}  // ✅ Pass the whole object
                      variant="icon"
                    />
                  </Box>
                )}

                {/* UPDATED: Tier Badge - adjust position if ranking present */}
                {creator.tier_level && creator.tier_level !== 'standard' && (
                  <TierBadge 
                    label={creator.tier_level} 
                    size="small" 
                    tier={creator.tier_level}
                    sx={{ 
                      top: creator.ranking_metadata ? 52 : 12, // Move down if ranking badge present
                    }}
                  />
                )}

              {/* Platform Indicator */}
              {platform && (
                <PlatformIndicator className="platform-indicator" platform={platform}>
                  {socialIcons[platform] || <LinkIcon fontSize="small" />}
                </PlatformIndicator>
              )}

              {/* Creator Avatar Section */}
              <Box sx={{ p: 2, pb: 1, textAlign: 'center', position: 'relative' }}>
                <Avatar
                  className="creator-avatar"
                  src={creator.pfphref || profilePhoto}
                  alt={creator.creator}
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    border: `3px solid ${theme.palette.background.paper}`,
                    boxShadow: theme.shadows[4],
                    transition: 'all 0.3s ease',
                  }}
                />

                <Link
                  href={`/creators/${creator.creator}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '1rem',
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main',
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  @{creator.display_name}
                </Link>

                {/* Rating */}
                <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  {avgReview > 0 ? (
                    <>
                      <StyledRating 
                        value={Math.round(avgReview)} 
                        readOnly 
                        size="small"
                      />
                      <Typography variant="caption" color="text.secondary">
                        ({reviewCount})
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No reviews yet
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Stats Section */}
              <CardContent sx={{ pt: 0, pb: 1 }}>
                <Stack spacing={1.5}>
                  {/* Followers */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FollowersIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatFollowers(followerCount)} followers
                    </Typography>
                  </Box>

                  {/* Average Views */}
                  {creator.avg_views && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ViewsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatNumber(creator.avg_views)} avg views
                      </Typography>
                    </Box>
                  )}

                  {/* Platform Links (for "All" mode) */}
                  {!platform && creatorPlatforms.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {creatorPlatforms.slice(0, 4).map((p) => (
                        <Tooltip key={p.platform} title={`View on ${p.platform}`}>
                          <IconButton
                            component="a"
                            href={p.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              color: platformColors[p.platform] || 'text.secondary',
                              '&:hover': {
                                backgroundColor: alpha(platformColors[p.platform] || theme.palette.primary.main, 0.1),
                              }
                            }}
                          >
                            {socialIcons[p.platform] || <LinkIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      ))}
                    </Box>
                  )}

                  {/* Platform Link (for specific platform) */}
                  {platform && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Link
                        href={
                          creator[`${platform.toLowerCase()}_link`] ||
                          `/creators/${creator.creator}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        View on {platform}
                        <ExternalLinkIcon fontSize="small" />
                      </Link>
                    </Box>
                  )}

                  {/* Pricing */}
                  {!hasCelebTier && (
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}
                      >
                        Suggested offer: {rate ? formatCurrency(rate, { decimals: 0 }) : 'Contact for pricing'}
                      </Typography>
                    </Box>
                  )}

                  {hasCelebTier && (
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Chip
                        label="Contact for custom pricing"
                        size="small"
                        sx={{
                          background: 'linear-gradient(45deg, #FFD700, #FFA500)',
                          color: '#000',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              </CardContent>

              {/* Action Buttons */}
              <Box
                className="action-buttons"
                sx={{
                  p: 2,
                  pt: 1,
                  display: 'flex',
                  gap: 1,
                  opacity: 0.7,
                  transform: 'translateY(4px)',
                  transition: 'all 0.3s ease',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant={isSelected ? "contained" : "outlined"}
                  color="primary"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddToCampaign(creator.creator);
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1,
                  }}
                >
                  {isSelected ? 'Selected' : 'Book'}
                </Button>

                {onChatWithCreator && !hasCelebTier && (
                  <Tooltip title="Chat with creator">
                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{
                        minWidth: 44,
                        px: 1,
                        borderRadius: 2,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChatWithCreator(creator.creator);
                      }}
                    >
                      <ChatIcon fontSize="small" />
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </ModernCard>
          </Grid>
        );
      })}
    </Grid>
  );
}

export default memo(CreatorContainers);